import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { listingService } from '../lib/databaseService';
import { ArrowLeft, Edit3, Trash2, Users, Building2, Wallet, ListChecks } from 'lucide-react';

export default function Cabinet({ user, listings = [], agencies = [], onUpdateListings, onRefreshListings }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [agency, setAgency] = useState(null);
  const [agents, setAgents] = useState([]);
  const [activeTab, setActiveTab] = useState('listings');
  const [editingAgentLimit, setEditingAgentLimit] = useState({});
  const [myListingsDb, setMyListingsDb] = useState(null);

  const userIdFallback = user?.id || null;

  const myListings = useMemo(() => {
    if (Array.isArray(myListingsDb)) return myListingsDb;
    const id = profile?.id || userIdFallback;
    return (listings || []).filter(l => l.userId === id);
  }, [listings, profile, userIdFallback, myListingsDb]);

  const loadData = useCallback(async () => {
    let isMounted = true;
    try {
      setLoading(true);
      const { data: authData } = await supabase.auth.getUser();
      const authId = authData?.user?.id || null;
      const targetId = authId || userIdFallback;
      if (!targetId) {
        if (isMounted) setLoading(false);
        return;
      }

      // Profile
      const { data: prof, error: profErr } = await supabase
        .from('users')
        .select('*')
        .eq('id', targetId)
        .single();
      if (!isMounted) return;
      if (profErr) {
        const activeCount = Array.isArray(listings) ? listings.filter(l => l?.userId === targetId).length : 0;
        setProfile({ id: targetId, role: 'user', listing_limit: 0, active_listings_count: activeCount, agency_id: null, name: user?.name || 'İstifadəçi' });
      } else {
        setProfile(prof);
      }

      // Listings (fetch only user's own as per RLS)
      try {
        const { data: myL, error: myLErr } = await supabase
          .from('listings')
          .select('*')
          .eq('user_id', targetId)
          .order('created_at', { ascending: false });
        if (isMounted && !myLErr) {
          setMyListingsDb(Array.isArray(myL) ? myL.map(item => ({
            id: item.id,
            image: Array.isArray(item.images) ? item.images[0] : null,
            type: item.type,
            location: item.location,
            userId: item.user_id
          })) : []);
        }
      } catch {}

      // Agency
      const agencyId = prof?.agency_id || null;
      if (agencyId) {
        const { data: ag, error: agErr } = await supabase
          .from('agencies')
          .select('*')
          .eq('id', agencyId)
          .single();
        if (isMounted && !agErr) setAgency(ag);
      } else {
        const owned = (agencies || []).find(a => a?.owner_id === targetId) || null;
        if (isMounted && owned) setAgency(owned);
      }

      // Agents for owner
      const effectiveRole = prof?.role || 'user';
      const effectiveAgencyId = prof?.agency_id || agency?.id || null;
      if (effectiveRole === 'agency_owner' && effectiveAgencyId) {
        const { data: agentRows, error: agentErr } = await supabase
          .from('users')
          .select('id, email, role, listing_limit')
          .eq('agency_id', effectiveAgencyId);
        if (isMounted && !agentErr && Array.isArray(agentRows)) {
          setAgents(agentRows.filter(a => a?.id !== (prof?.id || targetId)));
        }
      }
    } catch (e) {
      // ignore
    } finally {
      if (isMounted) setLoading(false);
    }
    return () => { isMounted = false; };
  }, [userIdFallback, user?.name, agency?.id, agencies, listings]); 
  // listings dependency-dən çıxarıldı - sonsuz dövrü qarşısını almaq üçün

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadData();
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIdFallback, user?.name, agency?.id]); 
  // loadData dependency-dən çıxarıldı - callback stabil olduğundan

  const role = (profile?.role) || 'user';
  const displayName = profile?.name || user?.name || 'İstifadəçi';

  const handleDeleteListing = async (id) => {
    try {
      await listingService.delete(id);
      toast.success('Elan uğurla silindi');
      if (onRefreshListings) {
        await onRefreshListings();
      } else if (onUpdateListings) {
        const filtered = (listings || []).filter(l => l.id !== id);
        onUpdateListings(filtered);
      }
    } catch (e) {
      toast.error('Elanı silmək mümkün olmadı');
    }
  };

  const startEditLimit = (agentId, currentLimit) => {
    setEditingAgentLimit(prev => ({ ...prev, [agentId]: String(currentLimit ?? 0) }));
  };

  const saveAgentLimit = async (agentId) => {
    const val = editingAgentLimit[agentId];
    const num = Number(val);
    if (Number.isNaN(num) || num < 0) {
      toast.error('Yanlış limit dəyəri');
      return;
    }
    try {
      const { error } = await supabase.from('users').update({ listing_limit: num }).eq('id', agentId);
      if (error) throw error;
      toast.success('Limit təyin edildi');
      setAgents(prev => prev.map(a => a.id === agentId ? { ...a, listing_limit: num } : a));
      setEditingAgentLimit(prev => {
        const copy = { ...prev };
        delete copy[agentId];
        return copy;
      });
    } catch (e) {
      toast.error('Limit təyin edilə bilmədi');
    }
  };

  const WalletPlaceholder = () => (
    <div className="rounded-lg border p-6 bg-white">
      <div className="flex items-center gap-3">
        <Wallet className="text-gray-500" size={20} />
        <h3 className="font-semibold">Pulqabı</h3>
      </div>
      <p className="text-sm text-gray-600 mt-3">Bu funksiya tezliklə aktiv olacaq</p>
    </div>
  );

  const ListingRow = ({ l }) => (
    <div className="flex items-center justify-between p-3 border rounded-lg bg-white">
      <div className="flex items-center gap-3">
        <div className="w-16 h-16 bg-gray-100 rounded overflow-hidden">
          {l.image ? (
            <img src={l.image} alt={l.type} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">Şəkil yoxdur</div>
          )}
        </div>
        <div>
          <p className="font-medium text-gray-900">{l.type || 'Elan'}</p>
          <p className="text-sm text-gray-500">{l.location}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={() => toast.info('Düzəliş funksiyası tezliklə aktiv olacaq')}>
          <Edit3 size={16} className="mr-2" /> Düzəliş et
        </Button>
        <Button variant="destructive" onClick={() => handleDeleteListing(l.id)}>
          <Trash2 size={16} className="mr-2" /> Sil
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-40 bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="outline" asChild>
              <Link to="/">
                <ArrowLeft size={18} className="mr-2" />
                Geri
              </Link>
            </Button>
            <span className="logo-text text-2xl font-semibold text-[#B91C1C]">Mydom</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Header by role */}
        <div className="mb-6">
          {role === 'agency_owner' ? (
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              {(agency?.name || 'Agentlik')} Rəhbər Paneli
            </h1>
          ) : role === 'agent' ? (
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Agent Paneli</h1>
          ) : (
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Xoş gəldiniz, {displayName}</h1>
          )}
          <p className="text-sm text-gray-500 mt-1">Hesab və elanlarınızı buradan idarə edin.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <div className="rounded-lg border p-4 bg-white">
            <div className="flex items-center gap-2 text-gray-700">
              <ListChecks size={18} />
              <span className="font-medium">Aktiv Elan sayı</span>
            </div>
            <div className="mt-3 text-2xl font-bold">{myListings.length}</div>
          </div>
          {role === 'agent' && (
            <div className="rounded-lg border p-4 bg-white">
              <div className="flex items-center gap-2 text-gray-700">
                <Building2 size={18} />
                <span className="font-medium">Qalan Elan Limiti</span>
              </div>
              <div className="mt-3 text-2xl font-bold">
                {Math.max((profile?.listing_limit ?? 0) - (profile?.active_listings_count ?? myListings.length), 0)}
              </div>
            </div>
          )}
          <div className="rounded-lg border p-4 bg-white">
            <div className="flex items-center gap-2 text-gray-700">
              <Wallet size={18} />
              <span className="font-medium">Pulqabı</span>
            </div>
            <div className="mt-3 text-sm text-gray-600">Bu funksiya tezliklə aktiv olacaq</div>
          </div>
        </div>

        {/* Tabs by role */}
        {role === 'agency_owner' ? (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="agents" className="mr-2"><Users size={16} className="mr-2" /> Agentlər</TabsTrigger>
              <TabsTrigger value="mylistings" className="mr-2">Mənim Elanlarım</TabsTrigger>
              <TabsTrigger value="balance">Agentlik Balansı</TabsTrigger>
            </TabsList>
            <TabsContent value="agents" className="mt-4">
              <div className="rounded-lg border bg-white">
                <div className="p-4 border-b flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users size={18} />
                    <h3 className="font-semibold">Agentlər</h3>
                  </div>
                </div>
                <div className="p-4 space-y-3">
                  {agents.length === 0 && (
                    <p className="text-sm text-gray-600">Agent tapılmadı.</p>
                  )}
                  {agents.map((a) => (
                    <div key={a.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{a.email || a.id}</p>
                        <p className="text-xs text-gray-500">Cari limit: {a.listing_limit ?? 0}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {editingAgentLimit[a.id] === undefined ? (
                          <Button variant="outline" onClick={() => startEditLimit(a.id, a.listing_limit)}>
                            Limit Təyin et
                          </Button>
                        ) : (
                          <>
                            <div className="flex items-center gap-2">
                              <Label htmlFor={`limit-${a.id}`} className="text-xs text-gray-600">Limit</Label>
                              <Input
                                id={`limit-${a.id}`}
                                type="number"
                                className="w-28"
                                value={editingAgentLimit[a.id]}
                                onChange={(e) => setEditingAgentLimit(prev => ({ ...prev, [a.id]: e.target.value }))}
                              />
                            </div>
                            <Button className="bg-[#B91C1C] hover:bg-[#991B1B]" onClick={() => saveAgentLimit(a.id)}>
                              Yadda saxla
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
            <TabsContent value="mylistings" className="mt-4">
              <div className="space-y-3">
                {myListings.length === 0 && (
                  <p className="text-sm text-gray-600">Elanınız yoxdur.</p>
                )}
                {myListings.map(l => <ListingRow key={l.id} l={l} />)}
              </div>
            </TabsContent>
            <TabsContent value="balance" className="mt-4">
              <WalletPlaceholder />
            </TabsContent>
          </Tabs>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="listings" className="mr-2">Elanlarım</TabsTrigger>
              <TabsTrigger value="wallet">Pulqabı</TabsTrigger>
            </TabsList>
            <TabsContent value="listings" className="mt-4">
              <div className="space-y-3">
                {myListings.length === 0 && (
                  <p className="text-sm text-gray-600">Elanınız yoxdur.</p>
                )}
                {myListings.map(l => <ListingRow key={l.id} l={l} />)}
              </div>
            </TabsContent>
            <TabsContent value="wallet" className="mt-4">
              <WalletPlaceholder />
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}
